import { useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useDriverStore } from '@/stores/driverStore'
import { useConfigStore } from '@/stores/configStore'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { setSentryUser } from '@/lib/sentry'
import { MainLayout } from '@/components/layout/MainLayout'
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout'
import { ScrollToTop } from '@/components/layout/ScrollToTop'
import { ProtectedRoute } from '@/components/routing/ProtectedRoute'
import { AdminRoute } from '@/components/routing/AdminRoute'
import { LoginPage } from '@/components/auth/LoginPage'
import { SalaryReportPage } from '@/pages/SalaryReportPage'
import { externalUrls } from '@/lib/constants'

import { ConfigurationPage } from '@/pages/ConfigurationPage'
import { DriverDetailPage } from '@/pages/DriverDetailPage'
import { UserProfilePage } from '@/pages/UserProfilePage'
import { UploadPage } from '@/pages/UploadPage'
import { UploadWorkflowPage } from '@/pages/UploadWorkflowPage'
import { DriverUploadPage } from '@/pages/DriverUploadPage'
import { DriverUploadWorkflowPage } from '@/pages/DriverUploadWorkflowPage'
import { ForarePage } from '@/pages/ForarePage'
import { VehiclesPage } from '@/pages/VehiclesPage'
import { BulkCompensationPage } from '@/pages/BulkCompensationPage'
import { TermsOfServicePage } from '@/pages/TermsOfServicePage'
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage'
import PeriodLockingTestPage from '@/pages/PeriodLockingTestPage'
import { PeriodListPage } from '@/pages/PeriodListPage'
import { PeriodDetailPage } from '@/pages/PeriodDetailPage'
import { WizardUploadStep } from '@/pages/wizard/WizardUploadStep'
import { WizardAdjustmentsStep } from '@/pages/wizard/WizardAdjustmentsStep'
import { WizardReviewStep } from '@/pages/wizard/WizardReviewStep'
import { PeriodExportPage } from '@/pages/PeriodExportPage'
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage'
import { AdminUserDetailPage } from '@/pages/admin/AdminUserDetailPage'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { Button } from '@/components/ui/button'
import { AlertTriangle } from 'lucide-react'

// Protected app content that requires authentication
function AppContent() {
  const { currentUser: user } = useAuth()
  const loadDrivers = useDriverStore(state => state.loadDrivers)
  const loadSettings = useConfigStore(state => state.loadSettings)
  const settings = useConfigStore(state => state.settings)
  const loadTaxTableIfNeeded = useConfigStore(state => state.loadTaxTableIfNeeded)

  // Load initial data when user is authenticated
  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        // Clear Sentry user context on logout
        setSentryUser(null)
        return
      }

      // Set Sentry user context for error tracking
      setSentryUser(user.uid, user.email)

      try {
        await Promise.all([
          loadDrivers(user),
          loadSettings(user)
        ])
      } catch (error) {
        console.error('Failed to load initial data:', error)
        // Loading failed
      }
    }
    loadData()
  }, [user, loadDrivers, loadSettings])

  // Load tax table automatically if user has table method selected
  useEffect(() => {
    if (!user || !settings) return

    loadTaxTableIfNeeded(settings.taxCalculationMethod, settings.taxTableName)
  }, [user, settings.taxCalculationMethod, settings.taxTableName, loadTaxTableIfNeeded])

  return (
    <>
      <Routes>
        {/* Default route shows salary report */}
        <Route path="/" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <SalaryReportPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        
        {/* Application routes with MainLayout and ProtectedRoute */}
        <Route path="/salaryreport" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <SalaryReportPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/driver/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <DriverDetailPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/configuration" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <ConfigurationPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <UserProfilePage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/upload" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <UploadPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/upload/workflow/:uploadId" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <UploadWorkflowPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/forare/upload" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <DriverUploadPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/forare/upload/workflow/:uploadId" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <DriverUploadWorkflowPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/forare" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <ForarePage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/vehicles" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <VehiclesPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/salaryreport/bulk-compensation" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <BulkCompensationPage />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Period management routes */}
        <Route path="/periods" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <PeriodListPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/periods/:periodId" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <PeriodDetailPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/periods/:periodId/upload" element={
          <ProtectedRoute>
            <WorkspaceLayout>
              <ScrollToTop />
              <WizardUploadStep />
            </WorkspaceLayout>
          </ProtectedRoute>
        } />
        <Route path="/periods/:periodId/adjustments" element={
          <ProtectedRoute>
            <WorkspaceLayout>
              <ScrollToTop />
              <WizardAdjustmentsStep />
            </WorkspaceLayout>
          </ProtectedRoute>
        } />
        <Route path="/periods/:periodId/review" element={
          <ProtectedRoute>
            <WorkspaceLayout>
              <ScrollToTop />
              <WizardReviewStep />
            </WorkspaceLayout>
          </ProtectedRoute>
        } />
        <Route path="/periods/:periodId/export" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <PeriodExportPage />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Admin routes - require the admin custom claim */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminRoute>
              <MainLayout>
                <ScrollToTop />
                <AdminUsersPage />
              </MainLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:uid" element={
          <ProtectedRoute>
            <AdminRoute>
              <MainLayout>
                <ScrollToTop />
                <AdminUserDetailPage />
              </MainLayout>
            </AdminRoute>
          </ProtectedRoute>
        } />

        {/* Developer test page for period locking */}
        <Route path="/test/period-locking" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <PeriodLockingTestPage />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Public pages (terms and privacy) accessible to authenticated users */}
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        {/* Catch-all route for authenticated users shows salary report */}
        <Route path="*" element={
          <ProtectedRoute>
            <MainLayout>
              <ScrollToTop />
              <SalaryReportPage />
            </MainLayout>
          </ProtectedRoute>
        } />
      </Routes>
      
    </>
  )
}

// Main app component with authentication routing
function AppRouter() {
  const { isAuthenticated, loading } = useAuth()

  // Redirect unauthenticated users accessing root to external landing page
  useEffect(() => {
    if (!loading && !isAuthenticated && window.location.pathname === '/') {
      window.location.href = externalUrls.landingPage
    }
  }, [loading, isAuthenticated])

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4">
            <SpinnerIcon size="lg" className="mx-auto text-blue-600" />
          </div>
          <p className="text-gray-600">Laddar...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public routes for non-authenticated users */}
      {!isAuthenticated && (
        <>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
        </>
      )}

      {/* Protected routes for authenticated users */}
      {isAuthenticated ? (
        <Route path="/*" element={<AppContent />} />
      ) : (
        <Route path="/*" element={<LoginPage />} />
      )}
    </Routes>
  )
}

function App() {
  // Initialize app
  useEffect(() => {
    const initializeApp = async () => {
      // App initialization
      // Note: DOM patches are applied in index.html before React starts
    }

    initializeApp()
  }, [])

  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 space-y-4">
            <div className="flex items-center gap-3 text-amber-600">
              <AlertTriangle className="h-8 w-8" />
              <h1 className="text-xl font-semibold">Något gick fel</h1>
            </div>

            <div className="space-y-2">
              <p className="text-gray-700">
                Ett oväntat fel har inträffat. Detta kan bero på:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-2">
                <li>Webbläsartillägg som Google Translate</li>
                <li>Nätverksproblem</li>
                <li>Ett tekniskt fel i applikationen</li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <p className="text-xs text-gray-600 mb-1 font-medium">Tips för att lösa problemet:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Inaktivera Google Translate eller andra översättningstillägg</li>
                <li>• Prova att ladda om sidan</li>
                <li>• Använd inkognitoläge (utan tillägg)</li>
              </ul>
            </div>

            <Button
              onClick={() => window.location.reload()}
              className="w-full"
            >
              Ladda om sidan
            </Button>
          </div>
        </div>
      }
      showDialog={false}
    >
      <AuthProvider>
        <TooltipProvider>
          <AppRouter />
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
